import { useState } from 'react';
import { api, exchangeFirebaseCustomToken, clearFirebaseSession } from '../lib/api.js';
import { ForgotPasswordModal } from './ForgotPasswordModal.jsx';

// ─── Login Screen ─────────────────────────────────────────────────────────────
export function LoginScreen({ onLogin }) {
  // Web build: password-only per CJ review §1. Staff contact info lives
  // behind this gate; a 4-digit PIN was too weak for that. The PIN-based
  // kiosk login lives in a separate iPad build (review §2) and is no
  // longer present on the web bundle.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  function doShake(msg) {
    setError(msg); setShake(true); setTimeout(() => setShake(false), 500);
  }

  async function handleStaffLogin(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      // Clear any stale Firebase token before starting — we'll get a fresh
      // one back from /api/login/email if the migration succeeds.
      clearFirebaseSession();
      const user = await api('POST', '/api/login/email', { email, password });
      // If the server returned a Firebase custom token, swap it for an ID
      // token and stash. Best-effort — failure leaves the session on the
      // legacy x-user-id path which the server still honors.
      if (user?.firebaseCustomToken && user?.firebaseApiKey) {
        await exchangeFirebaseCustomToken(user.firebaseCustomToken, user.firebaseApiKey);
      }
      onLogin(user);
    } catch (err) {
      // api() throws the response body as text. A sign-in the server couldn't
      // complete — Firebase stalled (sign_in_timeout, D303) or refused
      // (sign_in_unavailable, D306) — is not a wrong password; say what it is.
      let body = null;
      try { body = JSON.parse(err.message); } catch { /* not JSON: wrong-password path */ }
      const serverSays = body?.code === 'sign_in_timeout' || body?.code === 'sign_in_unavailable';
      doShake(serverSays ? body.error : 'Invalid email or password');
    }
    finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ink px-4">
      <div className="mb-8 text-center">
        <img src="assets/monarch-logo.png" alt="Monarch"
          style={{ width: 88, height: 'auto', margin: '0 auto 14px',
                    filter: 'brightness(0) invert(1) opacity(0.9)' }} />
        <div className="text-gold text-sm tracking-widest uppercase mb-2 font-medium">Empire Tea & Coffee</div>
        <h1 className="text-cream text-4xl font-bold tracking-tight">Monarch</h1>
        <p className="text-cream/50 mt-1 text-sm">Sign in</p>
      </div>

      <div className={`bg-cream rounded-2xl p-8 w-full max-w-sm shadow-2xl ${shake ? 'animate-bounce' : ''}`}>
        <form onSubmit={handleStaffLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</label>
            <input type="email" name="username" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white text-ink focus:outline-none focus:ring-2 focus:ring-crimson/30"
              placeholder="you@empireteaandcoffee.com" required autoFocus
              autoComplete="username" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Password</label>
            <input type="password" name="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white text-ink focus:outline-none focus:ring-2 focus:ring-crimson/30"
              placeholder="••••••••" required
              autoComplete="current-password" />
          </div>
          {error && <p className="text-crimson text-sm font-medium text-center">{error}</p>}
          <button type="submit" disabled={loading}
            style={{ background: 'var(--gold)', color: '#fff', fontWeight: 700,
                      width: '100%', padding: '12px', borderRadius: 12, border: 'none',
                      cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.5 : 1,
                      fontFamily: 'inherit', fontSize: 14 }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <p className="text-center" style={{ marginTop: 4 }}>
            <button type="button" onClick={() => setForgotOpen(true)}
              style={{ background: 'none', border: 'none', color: '#666', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 4 }}>
              Forgot password?
            </button>
          </p>
        </form>
      </div>

      <p className="text-cream/30 mt-8 text-xs">Authorized Personnel Only</p>
      {forgotOpen && <ForgotPasswordModal initialEmail={email} onClose={() => setForgotOpen(false)} />}
    </div>
  );
}
